import { PeriodSelection, UsageReportPeriod } from 'views/Admin/UsageReport/types';

// === API contract (mirrors RoiSummaryRequestDto / RoiSummaryResponseDto) ===

export type HitRateStatus = 'HEALTHY' | 'NEEDS_ATTENTION' | 'AT_RISK';

export interface RoiSummaryRequest {
  period?: UsageReportPeriod;
  /** ISO-8601 seconds-precision UTC: 'yyyy-MM-ddTHH:mm:ssZ'. Wins over period when both are set. */
  from?: string;
  to?: string;
  workspaceIds?: number[];
}

export interface TopServiceByVolumeApi {
  serviceMockId: number;
  serviceName: string;
  total: number;
}

export interface TopServiceByHitRateApi {
  serviceMockId: number;
  serviceName: string;
  hits: number;
  total: number;
  hitRate: number;
}

export interface HitsMissesBreakdownApi {
  serviceMockId: number;
  serviceName: string;
  hits: number;
  misses: number;
}

export interface IdleServiceApi {
  serviceMockId: number;
  serviceName: string;
  deployedAt: string | null;
  lastUsed: string | null;
}

export interface WorkspaceUsageApi {
  workspaceId: number;
  total: number;
}

export interface LocationUtilisationApi {
  locationId: number;
  locationName: string;
  total: number;
}

export interface RoiSummaryResponse {
  totalRequests: number;
  totalHits: number;
  totalMisses: number;
  /** Server-computed; the UI recomputes using effective hit rate. */
  hitRate: number;
  /** Server-computed; the UI recomputes using its own thresholds against effective hit rate. */
  hitRateStatus: HitRateStatus;
  activeServiceCount: number;
  totalServiceCount: number;
  topServicesByVolume: TopServiceByVolumeApi[];
  topServicesByHitRate: TopServiceByHitRateApi[];
  hitsMissesBreakdown: HitsMissesBreakdownApi[];
  idleServices: IdleServiceApi[];
  requestsByWorkspace: WorkspaceUsageApi[];
  locationUtilisation: LocationUtilisationApi[];
}

// === UI state ===

export interface CalculatorSettings {
  costPerCall: number;
}

export interface RoiFilters {
  period: PeriodSelection;
  workspaceIds: number[];
}

export interface RoiSummary {
  totalRequests: number;
  hitsServedByVs: number;
  estimatedCostAvoided: number;
  projectedAnnualSavings: number;
  effectiveHitRate: number;
  hitRateStatus: HitRateStatus;
  activeServiceCount: number;
  totalServiceCount: number;
}

export interface RoiServiceMetric {
  serviceName: string;
  hits: number;
  misses: number;
  total: number;
  hitRate: number;
}

export interface RoiWorkspaceMetric {
  workspaceName: string;
  totalRequests: number;
}

export interface RoiLocationMetric {
  locationName: string;
  totalRequests: number;
}

export interface IdleServiceEntry {
  serviceName: string;
  idleSinceLabel: string;
  idleSinceIso: string | null;
}

export interface RoiInsightsData {
  summary: RoiSummary;
  topByVolume: RoiServiceMetric[];
  topByHitRate: RoiServiceMetric[];
  hitMissBreakdown: RoiServiceMetric[];
  workspaceRequests: RoiWorkspaceMetric[];
  locationUtilisation: RoiLocationMetric[];
  idleCandidates: IdleServiceEntry[];
  dataAsOfIso: string;
}

export const DEFAULT_CALCULATOR_SETTINGS: CalculatorSettings = {
  costPerCall: 0.05,
};

export const DEFAULT_ROI_FILTERS: RoiFilters = {
  period: { kind: 'preset', value: 'LAST_QUARTER' },
  workspaceIds: [],
};
